import { SimpleGrid, TextInput } from "@mantine/core";
import { useState } from "react";
import { SectionCard } from "../HomeBlockShared";
import { DESIGN_IMAGE, HomeBlock } from "../homeBlockData";
import { BusinessConfig, IconLabel } from "../homeBlockConfig";
import {
  Crop,
  EditableList,
  EditorLayout,
  fieldSetter,
  IconPicker,
  ImagePicker,
  lucideIcon,
  newId,
  ThumbPicker,
  useBlockConfig,
} from "./EditorKit";

const IMG = DESIGN_IMAGE.business;
const BACKGROUND_CROP: Crop = { x: 960, y: 0, w: 320, h: 240 };

const iconLabel = (icon: string, label: string): IconLabel => ({
  id: newId(),
  icon: lucideIcon(icon),
  label,
});

const LOGO_NAMES = [
  "Vietcombank",
  "BIDV",
  "MB",
  "Toyota",
  "Samsung",
  "FPT Education",
  "Viettel",
  "ACB",
  "Techcombank",
  "Vingroup",
  "VNU",
  "VinUniversity",
  "RMIT",
  "British Council",
  "AEON",
  "Vinmec",
  "Bệnh viện Bạch Mai",
  "Bộ Công an",
  "Bộ Giáo dục và Đào tạo",
  "Tổng cục Thuế",
];

const LOGO_COLS = [481, 584, 686, 787, 888];
const LOGO_ROWS = [124, 153, 182, 211];
const SERVICE_X = [27, 177, 331, 487, 644, 801, 958, 1114];

const SAMPLE_SERVICES = [
  ["LAPTOP & MÁY TÍNH VĂN PHÒNG", "PC, Workstation, AIO, Mini PC", "/doanh-nghiep/may-tinh"],
  ["SERVER & STORAGE", "Máy chủ, lưu trữ NAS/SAN. Giải pháp Data Center", "/doanh-nghiep/server"],
  ["THIẾT BỊ MẠNG & WIFI", "Switch, Router, Firewall. Hệ thống mạng doanh nghiệp", "/doanh-nghiep/mang"],
  ["CAMERA GIÁM SÁT & AN NINH", "Camera IP, Analog. Giải pháp an ninh tổng thể", "/doanh-nghiep/camera"],
  ["THIẾT BỊ HỘI HỌP & TRÌNH CHIẾU", "Màn hình tương tác, máy chiếu. Giải pháp phòng họp", "/doanh-nghiep/hoi-hop"],
  ["PHẦN MỀM & BẢN QUYỀN", "Windows, Microsoft 365. Phần mềm chuyên ngành", "/doanh-nghiep/phan-mem"],
  ["AI WORKSTATION & GPU SERVER", "Máy trạm AI, GPU Server. Giải pháp AI cho doanh nghiệp", "/doanh-nghiep/ai"],
  ["HẠ TẦNG DATA CENTER", "Tủ rack, UPS, PDU. Hệ thống làm mát, triển khai", "/doanh-nghiep/data-center"],
];

// Ảnh mẫu cắt từ thiết kế: chỉ để xem khi chưa tải ảnh, không lưu
const SAMPLE_CROPS = new Map<string, Crop>([
  ...LOGO_NAMES.map((_, i): [string, Crop] => [
    `logo-${i + 1}`,
    { x: LOGO_COLS[i % 5], y: LOGO_ROWS[Math.floor(i / 5)], w: 95, h: 26 },
  ]),
  ...SAMPLE_SERVICES.map((_, i): [string, Crop] => [
    `svc-${i + 1}`,
    { x: SERVICE_X[i], y: 357, w: 148, h: 44 },
  ]),
]);

const initialConfig = (block: HomeBlock): BusinessConfig => {
  const sample = block.code === "home-business";
  return {
    heading: {
      eyebrow: sample ? "HACOM FOR BUSINESS" : "",
      subline: sample ? "Giải pháp công nghệ toàn diện cho doanh nghiệp Việt" : "",
      title: sample ? "KHÁCH HÀNG" : block.name,
      highlight: sample ? "DOANH NGHIỆP HACOM" : "",
      description: sample
        ? "Đồng hành cùng hàng nghìn tổ chức, doanh nghiệp và tập đoàn hàng đầu trên toàn quốc."
        : "",
      primaryButton: {
        label: sample ? "Giải pháp doanh nghiệp" : "",
        link: sample ? "/giai-phap-doanh-nghiep" : "",
      },
      secondaryButton: {
        label: sample ? "Liên hệ tư vấn" : "",
        link: sample ? "/lien-he-tu-van" : "",
      },
      backgroundImage: null,
    },
    industries: sample
      ? [
          iconLabel("landmark", "Ngân hàng Tài chính"),
          iconLabel("building-2", "Doanh nghiệp Tập đoàn"),
          iconLabel("graduation-cap", "Giáo dục Đào tạo"),
          iconLabel("hospital", "Y tế Bệnh viện"),
          iconLabel("building-2", "Cơ quan Nhà nước"),
        ]
      : [],
    logos: sample
      ? LOGO_NAMES.map((name, i) => ({ id: `logo-${i + 1}`, name, image: null, link: "" }))
      : [],
    benefits: sample
      ? [
          iconLabel("badge-percent", "Ưu đãi giá tốt"),
          iconLabel("user-check", "Chuyên viên hỗ trợ riêng"),
          iconLabel("file-text", "Hỗ trợ hồ sơ đấu thầu"),
          iconLabel("credit-card", "Thanh toán linh hoạt"),
          iconLabel("truck", "Giao hàng & triển khai tận nơi"),
          iconLabel("shield-check", "Bảo hành tận nơi"),
        ]
      : [],
    serviceGroups: sample
      ? SAMPLE_SERVICES.map(([title, description, link], i) => ({
          id: `svc-${i + 1}`,
          title,
          description,
          link,
          image: null,
        }))
      : [],
  };
};

const iconColumns = [
  { label: "Icon", width: "180px" },
  { label: "Nhãn", width: "minmax(0, 1fr)" },
];

const EditBusiness = ({ block }: { block: HomeBlock }) => {
  const [config, setConfig] = useBlockConfig(block, () => initialConfig(block));
  const { heading } = config;
  // Ảnh nền mẫu cắt từ thiết kế: chỉ để xem, gỡ thì ẩn; không nằm trong config
  const [showSampleBackground, setShowSampleBackground] = useState(block.code === "home-business");

  const patchHeading = (v: Partial<BusinessConfig["heading"]>) =>
    setConfig((c) => ({ ...c, heading: { ...c.heading, ...v } }));

  const renderIconCells = (item: IconLabel, update: (p: Partial<IconLabel>) => void) => (
    <>
      <IconPicker
        ariaLabel={`Icon ${item.label}`}
        value={item.icon}
        onChange={(value) => update({ icon: value })}
      />
      <TextInput
        aria-label="Nhãn"
        value={item.label}
        onChange={(e) => update({ label: e.currentTarget.value })}
      />
    </>
  );

  return (
    <EditorLayout block={block}>
      <SectionCard title="Đầu block">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
          <TextInput
            label="Nhãn nhỏ"
            value={heading.eyebrow}
            onChange={(e) => patchHeading({ eyebrow: e.currentTarget.value })}
          />
          <TextInput
            label="Dòng phụ cạnh nhãn"
            value={heading.subline}
            onChange={(e) => patchHeading({ subline: e.currentTarget.value })}
          />
          <TextInput
            label="Tiêu đề"
            value={heading.title}
            onChange={(e) => patchHeading({ title: e.currentTarget.value })}
          />
          <TextInput
            label="Phần tô màu trong tiêu đề"
            value={heading.highlight}
            onChange={(e) => patchHeading({ highlight: e.currentTarget.value })}
          />
        </SimpleGrid>
        <TextInput
          label="Mô tả"
          value={heading.description}
          onChange={(e) => patchHeading({ description: e.currentTarget.value })}
        />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
          <TextInput
            label="Nút chính"
            value={heading.primaryButton.label}
            onChange={(e) =>
              patchHeading({ primaryButton: { ...heading.primaryButton, label: e.currentTarget.value } })
            }
          />
          <TextInput
            label="Link nút chính"
            value={heading.primaryButton.link}
            onChange={(e) =>
              patchHeading({ primaryButton: { ...heading.primaryButton, link: e.currentTarget.value } })
            }
          />
          <TextInput
            label="Nút phụ"
            value={heading.secondaryButton.label}
            onChange={(e) =>
              patchHeading({
                secondaryButton: { ...heading.secondaryButton, label: e.currentTarget.value },
              })
            }
          />
          <TextInput
            label="Link nút phụ"
            value={heading.secondaryButton.link}
            onChange={(e) =>
              patchHeading({
                secondaryButton: { ...heading.secondaryButton, link: e.currentTarget.value },
              })
            }
          />
        </SimpleGrid>
        <ImagePicker
          label="Ảnh nền bên phải"
          src={heading.backgroundImage}
          imageId={showSampleBackground ? IMG : undefined}
          crop={BACKGROUND_CROP}
          onPick={(url) => patchHeading({ backgroundImage: url })}
          onClear={() => {
            setShowSampleBackground(false);
            patchHeading({ backgroundImage: null });
          }}
          height={120}
        />
      </SectionCard>

      <SectionCard title="Lĩnh vực khách hàng">
        <EditableList
          items={config.industries}
          onChange={fieldSetter(setConfig, "industries")}
          itemLabel={(f) => f.label}
          columns={iconColumns}
          renderCells={renderIconCells}
          newItem={() => ({ id: newId(), icon: null, label: "" })}
          addLabel="Thêm lĩnh vực"
          max={6}
          minWidth={420}
        />
      </SectionCard>

      <SectionCard title="Logo khách hàng tiêu biểu">
        <EditableList
          items={config.logos}
          onChange={fieldSetter(setConfig, "logos")}
          itemLabel={(l) => l.name}
          columns={[
            { label: "Logo", width: "96px" },
            { label: "Tên", width: "minmax(0, 1fr)" },
            { label: "Link (tuỳ chọn)", width: "minmax(0, 1fr)" },
          ]}
          renderCells={(l, update) => (
            <>
              <ThumbPicker
                label={l.name}
                src={l.image}
                imageId={IMG}
                crop={SAMPLE_CROPS.get(l.id)}
                onPick={(url) => update({ image: url })}
                width={96}
              />
              <TextInput
                aria-label="Tên khách hàng"
                value={l.name}
                onChange={(e) => update({ name: e.currentTarget.value })}
              />
              <TextInput
                aria-label={`Link ${l.name}`}
                placeholder="https://"
                value={l.link}
                onChange={(e) => update({ link: e.currentTarget.value })}
              />
            </>
          )}
          newItem={() => ({ id: newId(), name: "", image: null, link: "" })}
          addLabel="Thêm logo"
          max={20}
          minWidth={560}
        />
      </SectionCard>

      <SectionCard title="Quyền lợi khách hàng doanh nghiệp">
        <EditableList
          items={config.benefits}
          onChange={fieldSetter(setConfig, "benefits")}
          itemLabel={(f) => f.label}
          columns={iconColumns}
          renderCells={renderIconCells}
          newItem={() => ({ id: newId(), icon: null, label: "" })}
          addLabel="Thêm quyền lợi"
          max={6}
          minWidth={420}
        />
      </SectionCard>

      <SectionCard title="Các nhóm dịch vụ chính">
        <EditableList
          items={config.serviceGroups}
          onChange={fieldSetter(setConfig, "serviceGroups")}
          itemLabel={(s) => s.title}
          columns={[
            { label: "Ảnh", width: "96px" },
            { label: "Tiêu đề", width: "minmax(0, 1fr)" },
            { label: "Mô tả", width: "minmax(0, 1.3fr)" },
            { label: "Link", width: "minmax(0, 0.9fr)" },
          ]}
          renderCells={(s, update) => (
            <>
              <ThumbPicker
                label={s.title}
                src={s.image}
                imageId={IMG}
                crop={SAMPLE_CROPS.get(s.id)}
                onPick={(url) => update({ image: url })}
                width={96}
              />
              <TextInput
                aria-label="Tiêu đề nhóm dịch vụ"
                value={s.title}
                onChange={(e) => update({ title: e.currentTarget.value })}
              />
              <TextInput
                aria-label={`Mô tả ${s.title}`}
                value={s.description}
                onChange={(e) => update({ description: e.currentTarget.value })}
              />
              <TextInput
                aria-label={`Link ${s.title}`}
                value={s.link}
                onChange={(e) => update({ link: e.currentTarget.value })}
              />
            </>
          )}
          newItem={() => ({ id: newId(), title: "", description: "", link: "", image: null })}
          addLabel="Thêm nhóm dịch vụ"
          max={8}
          minWidth={720}
        />
      </SectionCard>
    </EditorLayout>
  );
};

export default EditBusiness;
